import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "next-themes";

const ViewProposalRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/proposal?id=${id}`} replace />;
};
const ViewMSARedirect = () => {
  const { token } = useParams();
  return <Navigate to={`/msa/${token}`} replace />;
};
import { SOWProvider } from "./contexts/SOWContext";
import { AuthProvider } from "./contexts/AuthContext";
import { lazy, Suspense } from "react";

const SocialProofToasts = lazy(() => import("./components/SocialProofToasts"));
const UnicornBackground = lazy(() => import("./components/UnicornBackground"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));
const Sonner = lazy(() => import("./components/ui/sonner").then(m => ({ default: m.Toaster })));

// Retry wrapper for lazy imports to handle stale chunk errors after rebuilds
const lazyWithRetry = (importFn: () => Promise<any>) =>
  lazy(() =>
    importFn().catch((err) => {
      const key = 'chunk_retry_' + btoa(err?.message?.slice(0, 50) || 'unknown');
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return new Promise(() => {});
      }
      console.error('Dynamic import failed after retry:', err);
      return { default: () => null };
    })
  );

const Index = lazyWithRetry(() => import("./pages/Index"));
const StatementOfWork = lazyWithRetry(() => import("./pages/StatementOfWork"));
const BackofficeLogin = lazyWithRetry(() => import("./pages/BackofficeLogin"));
const Backoffice = lazyWithRetry(() => import("./pages/Backoffice"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const SOWConfirmation = lazyWithRetry(() => import("./pages/SOWConfirmation"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const ViewProposal = lazyWithRetry(() => import("./pages/ViewProposal"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const AddOns = lazyWithRetry(() => import("./pages/AddOns"));
const About = lazyWithRetry(() => import("./pages/About"));
const Testimonials = lazyWithRetry(() => import("./pages/Testimonials"));
const Customize = lazyWithRetry(() => import("./pages/Customize"));
const StrategicPlanning = lazyWithRetry(() => import("./pages/StrategicPlanning"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const OnboardingThankYou = lazyWithRetry(() => import("./pages/OnboardingThankYou"));
const Account = lazyWithRetry(() => import("./pages/Account"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const ClientPortal = lazyWithRetry(() => import("./pages/ClientPortal"));
const ViewMSA = lazyWithRetry(() => import("./pages/ViewMSA"));
const ProjectRequest = lazyWithRetry(() => import("./pages/ProjectRequest"));
const ViewQuote = lazyWithRetry(() => import("./pages/ViewQuote"));
const Solutions = lazyWithRetry(() => import("./pages/Solutions"));
const PlanChangeRequest = lazyWithRetry(() => import("./pages/PlanChangeRequest"));
const Partner = lazyWithRetry(() => import("./pages/Partner"));
const Media = lazyWithRetry(() => import("./pages/Media"));
const DistributorPartner = lazyWithRetry(() => import("./pages/DistributorPartner"));
const OurPartners = lazyWithRetry(() => import("./pages/OurPartners"));
const Helpdesk = lazyWithRetry(() => import("./pages/Helpdesk"));
const WebsiteAnalysis = lazyWithRetry(() => import("./pages/WebsiteAnalysis"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark" enableSystem={false}>
      <TooltipProvider>
          <AuthProvider>
            <SOWProvider>
              <Suspense fallback={null}>
                <UnicornBackground />
                <SocialProofToasts />
              </Suspense>
              <Toaster />
              <Suspense fallback={null}>
                <Sonner />
              </Suspense>
              <BrowserRouter>
                <Suspense fallback={null}>
                  <ChatWidget />
                </Suspense>
                <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/add-ons" element={<AddOns />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/testimonials" element={<Testimonials />} />
                    <Route path="/strategic-planning" element={<StrategicPlanning />} />
                    <Route path="/customize" element={<Customize />} />
                    <Route path="/statement-of-work" element={<StatementOfWork />} />
                    <Route path="/backoffice/login" element={<BackofficeLogin />} />
                    <Route path="/backoffice" element={<Backoffice />} />
                    <Route path="/backoffice/reset-password" element={<ResetPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/sow-confirmation" element={<SOWConfirmation />} />
                    <Route path="/proposal" element={<ViewProposal />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/onboarding/thank-you" element={<OnboardingThankYou />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/portal" element={<ClientPortal />} />
                    <Route path="/msa/:token" element={<ViewMSA />} />
                    <Route path="/request" element={<ProjectRequest />} />
                    <Route path="/quote/:token" element={<ViewQuote />} />
                    <Route path="/solutions" element={<Solutions />} />
                    <Route path="/plan-change" element={<PlanChangeRequest />} />
                    <Route path="/partner" element={<Partner />} />
                    <Route path="/media" element={<Media />} />
                    <Route path="/distributor" element={<DistributorPartner />} />
                    <Route path="/partners" element={<OurPartners />} />
                    <Route path="/helpdesk" element={<Helpdesk />} />
                    <Route path="/analyze" element={<WebsiteAnalysis />} />
                    <Route path="/design-refinement" element={<Navigate to="/onboarding" replace />} />
                    <Route path="/view-proposal/:id" element={<ViewProposalRedirect />} />
                    <Route path="/view-msa/:token" element={<ViewMSARedirect />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </SOWProvider>
          </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
